import React, { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import { parseISO } from 'date-fns'
import { safeFormat } from '@/v2/utils/convert'
import Typography from '@/v2/components/common/Typography'
import Button from '@/v2/components/common/Button'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import { usePatientContext } from '@/v2/contexts/PatientContext'
import useModals from '@/v2/hooks/useModals'
import { useInsurances } from '@/v2/hooks/insurance/useInsurances'

const InsuranceCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
`

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
`

const InsuranceName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
`

const InsuranceCode = styled.div`
  font-size: 13px;
  color: #666;
`

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  gap: 16px;
`

const InfoLabel = styled.div`
  font-size: 13px;
  color: #666;
  flex-shrink: 0;
`

const InfoValue = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #333;
  text-align: right;
  word-break: break-word;
`

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
`

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 16px;
  color: #666;
  font-size: 14px;
  background: #fff;
  border-radius: 12px;
`

const LoadingState = styled.div`
  text-align: center;
  padding: 40px 16px;
  color: #666;
  font-size: 14px;
`

const InsurancesTabMobile = ({ onEligibilityCheckClick }) => {
  const { goTo } = useModals()
  const { id } = useParams()
  const { patient } = usePatientContext()
  const { insurances, isLoading, isError } = useInsurances(id)
  const { onOpenSnackbar } = useSnackbarContext()

  useEffect(() => {
    if (!isLoading && !!isError) {
      onOpenSnackbar(
        "It wasn't possible to load the patient insurances.",
        'error'
      )
    }
  }, [isLoading, isError, onOpenSnackbar])

  if (isLoading) {
    return <LoadingState>Loading...</LoadingState>
  }

  if (insurances.length === 0) {
    return <EmptyState>No insurances found</EmptyState>
  }

  const handleEligibilityCheck = insurance => {
    onEligibilityCheckClick()
    goTo(`/v2/patient/${patient.id}/eligibility/check`, {
      modalType: 'create',
      patient,
      insurance: { ...insurance.payer },
    })
  }

  const handleView = insurance => {
    goTo(`/v2/patient/${patient.id}/insurance/view`, {
      modalType: 'view',
      insurance,
      patient,
    })
  }

  return (
    <>
      {insurances.map(insurance => (
        <InsuranceCard key={insurance.id}>
          <CardHeader>
            <div>
              <InsuranceName>{insurance.payer.name}</InsuranceName>
              <InsuranceCode>ID: {insurance.payer.code}</InsuranceCode>
            </div>
          </CardHeader>

          <InfoRow>
            <InfoLabel>Created</InfoLabel>
            <InfoValue>
              {safeFormat(parseISO(insurance.createdAt), 'MMM dd, yyyy')}
            </InfoValue>
          </InfoRow>

          <CardActions>
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleEligibilityCheck(insurance)}
              fullWidth
            >
              Eligibility Check
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleView(insurance)}
              fullWidth
            >
              View
            </Button>
          </CardActions>
        </InsuranceCard>
      ))}
    </>
  )
}

export default InsurancesTabMobile
